import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Dialog, ErrorState, Input } from '../../components/index.js';
import { isActiveGameConflict } from '../../lib/active-game-conflict.js';
import { getErrorMessage } from '../../lib/errors.js';
import { useJoinSessionByCode } from '../../lib/games.js';

export interface JoinByCodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Passed down from the page so a start/join refused with
   * `ACTIVE_SESSION_EXISTS` (APP_SPEC §21.1) opens the shared
   * resume-or-abandon dialog instead of this dialog's own error banner.
   * Returns `true` when it handled the error.
   */
  onActiveGameConflict?: (error: unknown, retry: () => void) => boolean;
}

/**
 * The generic "join with a code" dialog (docs/games/word-rush/GAME_PLAN.md
 * §11, W3) — platform code with no game-specific conditional (CLAUDE.md
 * §2): the server alone knows which game/session a code belongs to. An
 * invalid, closed, full, or block-affected code is deliberately
 * indistinguishable (D7) — this dialog shows one generic error message for
 * every failure reason, never confirming which applied.
 */
export function JoinByCodeDialog({
  open,
  onOpenChange,
  onActiveGameConflict,
}: JoinByCodeDialogProps) {
  const navigate = useNavigate();
  const joinByCode = useJoinSessionByCode();
  const [code, setCode] = useState('');

  function join(joinCode: string) {
    joinByCode.mutate(
      { joinCode },
      {
        onSuccess: (session) => {
          onOpenChange(false);
          void navigate(`/play/${session.id}`);
        },
        // Deliberately stays open behind the shared conflict dialog: the
        // retry re-runs this mutation, which only works while this
        // component is still mounted.
        onError: (error) => onActiveGameConflict?.(error, () => join(joinCode)),
      },
    );
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (code.trim().length === 0) return;
    join(code.trim());
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Join with a code"
      description="Enter the 6-character code your host shared with you."
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Input
          label="Game code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          maxLength={6}
          autoCapitalize="characters"
          autoComplete="off"
          className="text-center font-display text-2xl tracking-[0.3em]"
        />
        {joinByCode.isError && !isActiveGameConflict(joinByCode.error) && (
          <ErrorState message={getErrorMessage(joinByCode.error)} />
        )}
        <Button type="submit" isLoading={joinByCode.isPending} disabled={code.trim().length === 0}>
          Join
        </Button>
      </form>
    </Dialog>
  );
}
